import React from 'react';
import '@/App.css';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './lib/auth';
import { PresenceProvider } from './lib/presence';
import { ThemeProvider } from './context/ThemeContext';
import { Toaster } from './components/ui/sonner';
import Login from './pages/Login';

import Signup from './pages/Signup';
import AuthCallback from './pages/AuthCallback';
import AcceptInvite from './pages/AcceptInvite';
import ResetPassword from './pages/ResetPassword';
import WorkspaceActivity from './pages/WorkspaceActivity';
import Rewards from './pages/Rewards';
import AppLayout from './pages/AppLayout';
import Dashboard from './pages/Dashboard';
import Projects from './pages/Projects';
import ProjectBoard from './pages/ProjectBoard';
import Team from './pages/Team';
import Roles from './pages/Roles';
import Departments from './pages/Departments';
import Designations from './pages/Designations';
import Reports from './pages/Reports';
import Settings from './pages/Settings';
import CommandCenter from './pages/CommandCenter';
import Chat from './pages/Chat';
import GoogleCalendar from './pages/GoogleCalendar';
import SuperAdmin from './pages/SuperAdmin';


function PendingApproval() {
  const { logout } = useAuth();
  return (
    <div className="tm-board-bg flex items-center justify-center p-4">
      <div className="tm-modal text-center" style={{ maxWidth: 440, padding: '40px 30px' }}>
        <div style={{ fontSize: 54, marginBottom: 20 }}>⏳</div>
        <h2 style={{ fontSize: 24, fontWeight: 700, color: 'var(--trello-blue-dark)', margin: '0 0 16px' }}>Approval Pending</h2>
        <p style={{ color: 'var(--trello-text-muted)', fontSize: 15, lineHeight: '22px', marginBottom: 28 }}>
          Your request to join the workspace is pending approval from the Leader. Once approved, you will be assigned a role and granted access to the workspace.
        </p>
        <button onClick={logout} className="tm-btn w-full" style={{ padding: '12px' }}>Log out</button>
      </div>
    </div>
  );
}

function Protected({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <div className="tm-board-bg flex items-center justify-center"><div className="tm-loader" style={{ borderColor: 'rgba(255,255,255,0.3)', borderTopColor: '#fff' }} /></div>;
  if (!user) return <Navigate to="/login" replace state={{ from: location }} />;
  if (user.status === 'pending' && !user.is_super_admin) return <PendingApproval />;
  return children;
}

function RoleProtected({ roles, children }) {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  const hasRole = roles.some(r => r.toLowerCase() === (user.role || '').toLowerCase());
  if (!hasRole) return <Navigate to="/dashboard" replace />;
  return children;
}

function SuperAdminProtected({ children }) {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  if (!user.is_super_admin) return <Navigate to="/dashboard" replace />;
  return children;
}

function AppRouter() {
  const location = useLocation();
  // Handle Emergent OAuth callback in URL fragment BEFORE regular routes
  if (location.hash?.includes('session_id=')) return <AuthCallback />;

  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/signup" element={<Signup />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/accept-invite" element={<AcceptInvite />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route element={<Protected><AppLayout /></Protected>}>
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/projects" element={<Projects />} />
        <Route path="/projects/:id" element={<ProjectBoard />} />
        <Route path="/chat" element={<Chat />} />
        <Route path="/google-calendar" element={<GoogleCalendar />} />

        <Route path="/command-center" element={<CommandCenter />} />
        <Route path="/team" element={<RoleProtected roles={['Leader', 'Manager']}><Team /></RoleProtected>} />
        <Route path="/roles" element={<RoleProtected roles={['Leader']}><Roles /></RoleProtected>} />
        <Route path="/departments" element={<Departments />} />
        <Route path="/designations" element={<Designations />} />
        <Route path="/reports" element={<Reports />} />
        <Route path="/activity" element={<WorkspaceActivity />} />
        <Route path="/rewards" element={<Rewards />} />
        <Route path="/settings" element={<RoleProtected roles={['Leader']}><Settings /></RoleProtected>} />
        <Route path="/super-admin" element={<SuperAdminProtected><SuperAdmin /></SuperAdminProtected>} />
      </Route>
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <ThemeProvider>
      <div className="App">
        <BrowserRouter>
          <AuthProvider>
            <PresenceProvider>
              <AppRouter />
              <Toaster />
            </PresenceProvider>
          </AuthProvider>
        </BrowserRouter>
      </div>
    </ThemeProvider>
  );
}
